import React from 'react';
import api from '../../services/api';

// Modal de succès de livraison avec téléchargement et prévisualisation
const LivraisonSuccessModal = ({ show, onClose, souscriptionReference, documentPath, downloadFilename }) => {
  const handleDownload = async () => {
    try {
      // Extraire l'ID de souscription depuis le documentPath
      const souscriptionId = documentPath.match(/\/souscriptions\/(\d+)\/document-livrable/)?.[1];

      if (!souscriptionId) {
        throw new Error('ID de souscription non trouvé dans le chemin du document');
      }

      console.log('📥 Téléchargement du document pour souscription:', souscriptionId);

      // Utiliser l'API service avec authentification
      const response = await api.get(`/souscriptions/${souscriptionId}/document-livrable`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      if (!response.data) {
        throw new Error('Aucune donnée reçue du serveur');
      }

      // Créer le blob et déclencher le téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename || `attestation_${souscriptionReference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Téléchargement réussi:', link.download);
    } catch (error) {
      console.error('Erreur de téléchargement:', error);
      alert(`Erreur lors du téléchargement du document: ${error.message}`);
    }
  };

  const handleView = async () => {
    try {
      // Extraire l'ID de souscription depuis le documentPath
      const souscriptionId = documentPath.match(/\/souscriptions\/(\d+)\/document-livrable/)?.[1];

      if (!souscriptionId) {
        throw new Error('ID de souscription non trouvé dans le chemin du document');
      }

      console.log('👁️ Visualisation du document pour souscription:', souscriptionId);

      // Utiliser l'API service avec authentification pour obtenir le PDF
      const response = await api.get(`/souscriptions/${souscriptionId}/document-livrable`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      if (!response.data) {
        throw new Error('Aucune donnée reçue du serveur');
      }

      // Créer une URL blob et l'ouvrir dans un nouvel onglet
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Ouvrir dans un nouvel onglet
      const newWindow = window.open(url, '_blank');

      // Nettoyer l'URL après un délai pour laisser le temps au navigateur de charger
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      if (!newWindow) {
        throw new Error('Impossible d\'ouvrir un nouvel onglet. Vérifiez que les popups ne sont pas bloqués.');
      }

      console.log('✅ Document ouvert avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'ouverture:', error);
      alert(`Erreur lors de l'ouverture du document: ${error.message}`);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl transform animate-slideIn">
        {/* Header */}
        <div className="px-6 py-4 border-b border-green-200 bg-gradient-to-r from-green-50 to-green-100 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  Livraison Réussie
                </h3>
                <p className="text-sm text-green-600">
                  Référence: {souscriptionReference}
                </p>
              </div>
            </div>
            {/* Bouton fermer */}
            <button
              onClick={onClose}
              className="text-green-400 hover:text-green-600 transition-colors duration-200 bg-white rounded-full p-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Attestation générée avec succès
            </h4>
            <p className="text-sm text-gray-600 mb-6">
              L'attestation d'hébergement et de prise en charge a été générée et la souscription a été marquée comme livrée. L'attestation a été automatiquement envoyée par email au client.
            </p>
            
            <div className="space-y-3">
              {/* Bouton Voir l'attestation */}
              <button
                onClick={handleView}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 shadow-md font-medium flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                <span>Voir l'attestation</span>
              </button>

              {/* Bouton Télécharger */}
              <button
                onClick={handleDownload}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-lg hover:from-blue-600 hover:to-green-600 transition-all duration-200 shadow-md font-medium flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>Télécharger l'attestation</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal d'erreur de livraison
const LivraisonErrorModal = ({ show, onClose, errorMessage, souscriptionReference }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl transform animate-slideIn">
        {/* Header */}
        <div className="px-6 py-4 border-b border-red-200 bg-gradient-to-r from-red-50 to-red-100 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-900">
                Erreur de Livraison
              </h3>
              <p className="text-sm text-red-600">
                Référence: {souscriptionReference}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Échec de la livraison
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Une erreur s'est produite lors de la génération de l'attestation ou de la livraison de la souscription.
            </p>
            
            {/* Message d'erreur détaillé */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <h5 className="text-sm font-medium text-red-800 mb-2">Détail de l'erreur:</h5>
              <p className="text-sm text-red-700 break-words">
                {errorMessage || "Erreur inconnue lors de la livraison"}
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={onClose}
                className="w-full px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loader de livraison
const LivraisonLoader = ({ show, souscriptionReference }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl transform">
        {/* Header */}
        <div className="px-6 py-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 to-orange-50 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900">
                Livraison en cours...
              </h3>
              <p className="text-sm text-blue-600">
                Référence: {souscriptionReference}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <svg className="w-8 h-8 text-blue-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Génération de l'attestation
            </h4>
            <p className="text-sm text-gray-600">
              Veuillez patienter pendant que nous générons l'attestation d'hébergement et de prise en charge...
            </p>
            
            {/* Barre de progression animée */}
            <div className="mt-6 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-orange-500 h-2 rounded-full animate-pulse transform origin-left animate-progress"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { LivraisonSuccessModal, LivraisonErrorModal, LivraisonLoader };